import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { 
    Clock, 
    Zap, 
    Settings2, 
    ShieldAlert, 
    UserCheck, 
    Target,
    ArrowUpRight,
    CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CTAButton from '../CTAButton';

const WhatWeSolve = () => {
    const navigate = useNavigate();
    const solutions = [
        {
            h: "Hiring Delays Are Slowing Operational Momentum",
            icon: <Clock size={24} />,
            content: "Open critical roles delay timelines, create operational pressure, and reduce productivity due to unscalable recruitment systems.",
            improve: ["Faster recruitment execution", "Structured hiring workflows", "Improved sourcing efficiency", "Workforce continuity"],
            color: "#38bdf8"
        },
        {
            h: "Rapid Business Growth Is Outpacing Infrastructure",
            icon: <Zap size={24} />,
            content: (
                <>
                    Startups and <a href="http://localhost:3000/industries/gcc" className="text-blue-600 underline">GCCs</a> scaling faster than their systems can support face workforce instability without structured hiring alignment.
                </>
            ),
            improve: ["Scalable workforce infrastructure", "High-volume hiring capability", "Process standardization", "Workforce planning systems"],
            color: "#818cf8"
        },
        {
            h: "Fragmented HR Operations Are Reducing Efficiency",
            icon: <Settings2 size={24} />,
            content: "Disconnected recruitment, payroll, and compliance create inefficiencies, forcing internal HR teams to manage complexity rather than strategy.",
            improve: ["Workforce process integration", "Streamlined HR operations", "Recruitment & payroll coordination", "Administrative efficiency"],
            color: "#2dd4bf"
        },
        {
            h: "Payroll & Compliance Complexity Is Increasing Risk",
            icon: <ShieldAlert size={24} />,
            content: "Growing teams face financial exposure from payroll errors and compliance gaps when using fragmented legacy systems.",
            improve: ["Payroll accuracy", "Statutory compliance management", "PF & ESI coordination", "Compliance governance"],
            color: "#fbbf24"
        },
        {
            h: "Generic Recruitment Models Are Producing Poor Quality",
            icon: <UserCheck size={24} />,
            content: "Volume-based traditional hiring ignores workforce alignment, resulting in weak retention and productivity losses.",
            improve: ["Role-aligned recruitment", "Workforce quality consistency", "Candidate evaluation structure", "Long-term workforce fit"],
            color: "#f87171"
        },
        {
            h: "Businesses Lack Long-Term Workforce Strategy",
            icon: <Target size={24} />,
            content: "Reactive hiring without scalable planning models leads to inconsistent execution and chaotic business expansion.",
            improve: ["Strategic workforce planning", "Long-term hiring visibility", "Scalability frameworks", "Sustainable growth systems"],
            color: "#a855f7"
        }
    ];

    return (
        <StyledSection>
            <div className="container mx-auto px-6 relative z-10">
                {/* Header */}
                <div className="max-w-4xl mb-24">
                    <motion.p 
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        className="font-black tracking-[0.4em] text-xs uppercase mb-6"
                        style={{ color: '#01014C' }}
                    >
                        Problem vs Solution
                    </motion.p>
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-3xl md:text-5xl font-bold syne text-white leading-tight mb-8"
                    >
                        What We Solve For <br/>
                        <span style={{ color: '#01014C' }}>Fast-Growing Businesses?</span>
                    </motion.h2>
                    <motion.p 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-white/80 text-sm md:text-lg max-w-2xl leading-relaxed"
                    >
                        We help organizations build stronger workforce foundations capable of supporting sustainable operational growth through strategically aligned solutions.
                    </motion.p>
                </div>

                {/* Vertical Interactive Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {solutions.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.7 }}
                            className="group h-full"
                        >
                            <SolutionCard activeColor={item.color}>
                                <div className="card-inner">
                                    <div className="header-row">
                                        <div className="icon-box">{item.icon}</div>
                                        <ArrowUpRight className="arrow-icon" size={20} />
                                    </div>
                                    
                                    <h3 className="card-title">{item.h}</h3>
                                    <p className="card-desc">{item.content}</p>

                                    <div className="improvement-section">
                                        <span className="improve-label">IMPROVEMENT FOCUS:</span>
                                        <div className="improve-grid">
                                            {item.improve.map((point, i) => (
                                                <div key={i} className="improve-item">
                                                    <CheckCircle2 size={12} className="check-icon" />
                                                    {point}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Bottom Glow Bar */}
                                    <div className="glow-bar" />
                                </div>
                            </SolutionCard>
                        </motion.div>
                    ))}
                </div>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="mt-16 flex justify-center w-full"
                >
                    <CTAButton 
                        text="Explore Solutions"
                        onClick={() => navigate('/contact')}
                        type="white"
                    />
                </motion.div>

            </div>
        </StyledSection>
    );
};

const StyledSection = styled.section`
    background-color: #2563EB;
    padding: 120px 0;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
        background: radial-gradient(circle at 50% 50%, rgba(56, 189, 248, 0.05) 0%, transparent 70%);
        pointer-events: none;
    }
`;

const SolutionCard = styled.div`
    background: #01014C;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 2rem;
    height: 100%;
    position: relative;
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
    cursor: default;

    .card-inner {
        padding: 40px;
        height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 2;
    }

    .header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 30px;
    }

    .icon-box {
        width: 50px;
        height: 50px;
        border-radius: 14px;
        background: rgba(255, 255, 255, 0.05);
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${props => props.activeColor};
        transition: all 0.5s ease;
    }

    .arrow-icon {
        color: rgba(255, 255, 255, 0.2);
        transition: all 0.5s ease;
    }

    .card-title {
        color: white;
        font-size: 20px;
        font-weight: 600;
        line-height: 1.4;
        margin-bottom: 20px;
        font-family: 'Syne', sans-serif;
    }

    .card-desc {
        color: white;
        font-size: 15px;
        line-height: 1.6;
        margin-bottom: 30px;
        flex-grow: 1;
    }

    .improvement-section {
        opacity: 0.6;
        transition: all 0.5s ease;
        transform: translateY(10px);
    }

    .improve-label {
        display: block;
        font-size: 10px;
        font-weight: 900;
        letter-spacing: 2px;
        color: ${props => props.activeColor};
        margin-bottom: 15px;
    }

    .improve-grid {
        display: grid;
        grid-template-cols: 1fr;
        gap: 10px;
    }

    .improve-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: white;
        font-size: 12px;
        font-weight: 600;
    }

    .check-icon {
        color: ${props => props.activeColor};
        flex-shrink: 0;
    }

    .glow-bar {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 4px;
        background: ${props => props.activeColor};
        transform: scaleX(0);
        transition: transform 0.5s ease;
        transform-origin: left;
    }

    /* Hover States */
    &:hover {
        background: #01014C;
        border: 1px solid rgba(255, 255, 255, 0.2);
        transform: translateY(-10px);
        box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.5);

        .icon-box {
            background: ${props => props.activeColor};
            color: white;
            box-shadow: 0 0 20px ${props => props.activeColor}55;
        }

        .arrow-icon {
            color: white;
            transform: translate(3px, -3px);
        }

        .card-desc {
            color: rgba(255, 255, 255, 0.8);
        }

        .improvement-section {
            opacity: 1;
            transform: translateY(0);
        }

        .glow-bar {
            transform: scaleX(1);
        }
    }
`;

export default WhatWeSolve;



// import React from 'react';
// import { motion } from 'framer-motion';
// import styled from 'styled-components';
// import { 
//     Zap, 
//     ArrowRight, 
//     Binary, 
//     Activity, 
//     ShieldCheck, 
//     Maximize,
//     Workflow
// } from 'lucide-react';

// const WhatWeSolve = () => {
//     const solutions = [
//         {
//             h: "Hiring Delays & Operational Momentum",
//             icon: <Binary size={24} />,
//             content: "Critical roles remaining open create operational pressure and delay execution. We fix recruitment systems that lack scalability.",
//             improve: ["Faster execution", "Structured workflows", "Sourcing efficiency", "Better coordination", "Reduced vacancy", "Workforce continuity"],
//             color: "#00f2ff"
//         },
//         {
//             h: "Infrastructure vs. Rapid Business Growth",
//             icon: <Maximize size={24} />,
//             content: "Startups and GCCs scale faster than systems support. We provide the structure to prevent growth-induced instability.",
//             improve: ["Scalable infra", "High-volume capability", "Multi-location setup", "Standardization", "Planning systems", "Expansion readiness"],
//             color: "#7000ff"
//         },
//         {
//             h: "Fragmented HR & Operational Inefficiency",
//             icon: <Workflow size={24} />,
//             content: "Disconnected payroll and compliance create compound inefficiencies. We free HR teams to focus on strategy, not paperwork.",
//             improve: ["Process integration", "Streamlined ops", "Payroll coordination", "Admin efficiency", "Management visibility", "Operational structure"],
//             color: "#ff007a"
//         },
//         {
//             h: "Payroll Complexity & Operational Risk",
//             icon: <ShieldCheck size={24} />,
//             content: "Inaccuracies and compliance gaps create financial exposure. We turn compliance from a risk into a governance process.",
//             improve: ["Payroll accuracy", "Statutory compliance", "PF & ESI management", "Contractor workflows", "Doc systems", "Compliance governance"],
//             color: "#ccff00"
//         },
//         {
//             h: "Generic Models & Poor Hiring Quality",
//             icon: <Activity size={24} />,
//             content: "Volume-based hiring leads to weak retention. We prioritize workforce alignment and role-specific operational fit.",
//             improve: ["Role-alignment", "Quality consistency", "Evaluation structure", "Talent acquisition", "Leadership precision", "Long-term fit"],
//             color: "#ff8a00"
//         },
//         {
//             h: "Lack of Long-Term Workforce Strategy",
//             icon: <Zap size={24} />,
//             content: "Reactive hiring creates chaotic expansion. We implement scalable frameworks aligned with long-term business goals.",
//             improve: ["Strategic planning", "Hiring visibility", "Scalability frameworks", "Org alignment", "Process maturity", "Sustainable growth"],
//             color: "#00ff88"
//         }
//     ];

//     return (
//         <SectionContainer>
//             <div className="container mx-auto px-6 relative z-10">
                
//                 {/* HEADER */}
//                 <div className="max-w-4xl mb-32">
//                     <motion.div
//                         initial={{ opacity: 0, x: -50 }}
//                         whileInView={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.8 }}
//                     >
//                         <p className="text-blue-400 font-mono tracking-[0.5em] text-xs uppercase mb-6 italic">/// System Diagnostics</p>
//                         <h2 className="text-5xl md:text-8xl font-bold syne text-white leading-none mb-10">
//                             Workforce <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-900/40">Bottlenecks.</span>
//                         </h2>
//                         <p className="text-blue-100/40 text-lg md:text-xl max-w-xl leading-relaxed border-l border-blue-500/30 pl-8">
//                             Merida HR Consulting builds stronger foundations for sustainable growth through performance-driven consulting.
//                         </p>
//                     </motion.div>
//                 </div>

//                 {/* THE KINETIC DRAWER GRID */}
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
//                     {solutions.map((item, index) => (
//                         <DrawerCard key={index} accent={item.color}>
//                             <motion.div 
//                                 className="drawer-wrapper"
//                                 initial={{ opacity: 0, y: 30 }}
//                                 whileInView={{ opacity: 1, y: 0 }}
//                                 viewport={{ once: true }}
//                                 transition={{ delay: index * 0.1 }}
//                             >
//                                 {/* The Visible Header Card */}
//                                 <div className="card-top">
//                                     <div className="icon-ring">{item.icon}</div>
//                                     <h3 className="title">{item.h}</h3>
//                                     <div className="corner-arrow">
//                                         <ArrowRight size={16} />
//                                     </div>
//                                 </div>

//                                 {/* The "Drawer" that unfolds on hover */}
//                                 <div className="drawer-content">
//                                     <div className="inner-padding">
//                                         <p className="desc">{item.content}</p>
//                                         <div className="improve-list">
//                                             {item.improve.map((point, i) => (
//                                                 <div key={i} className="list-item">
//                                                     <div className="dot" /> {point}
//                                                 </div>
//                                             ))}
//                                         </div>
//                                     </div>
//                                 </div>

//                                 {/* Animated Scanner Line */}
//                                 <div className="scanner-line" />
//                             </motion.div>
//                         </DrawerCard>
//                     ))}
//                 </div>

//             </div>
//         </SectionContainer>
//     );
// };

// const SectionContainer = styled.section`
//     background-color: #01014C;
//     padding: 150px 0;
//     overflow: hidden;
//     position: relative;

//     &::after {
//         content: 'SOLUTIONS';
//         position: absolute;
//         bottom: -50px;
//         right: -20px;
//         font-size: 15vw;
//         font-weight: 900;
//         color: rgba(255,255,255,0.02);
//         letter-spacing: -0.05em;
//     }
// `;

// const DrawerCard = styled.div`
//     perspective: 1000px;
//     height: 100%;

//     .drawer-wrapper {
//         position: relative;
//         background: rgba(255, 255, 255, 0.03);
//         border: 1px solid rgba(255, 255, 255, 0.05);
//         border-radius: 24px;
//         transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
//         transform-origin: top center;
//         cursor: pointer;
//     }

//     .card-top {
//         padding: 35px;
//         position: relative;
//         z-index: 10;
//         min-height: 220px;
//         display: flex;
//         flex-direction: column;
//         justify-content: space-between;
//     }

//     .icon-ring {
//         width: 45px;
//         height: 45px;
//         border-radius: 12px;
//         background: rgba(255,255,255,0.05);
//         display: flex;
//         align-items: center;
//         justify-content: center;
//         color: ${props => props.accent};
//         transition: 0.3s;
//     }

//     .title {
//         color: white;
//         font-size: 19px;
//         font-weight: 800;
//         line-height: 1.3;
//         font-family: 'Syne', sans-serif;
//         margin-top: 20px;
//     }

//     .corner-arrow {
//         position: absolute;
//         top: 35px;
//         right: 35px;
//         color: rgba(255,255,255,0.2);
//         transition: 0.4s;
//     }

//     /* THE HIDDEN DRAWER */
//     .drawer-content {
//         max-height: 0;
//         overflow: hidden;
//         transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
//         opacity: 0;
//         transform: rotateX(-20deg);
//         transform-origin: top;
//     }

//     .inner-padding {
//         padding: 0 35px 35px 35px;
//     }

//     .desc {
//         color: rgba(255,255,255,0.5);
//         font-size: 14px;
//         line-height: 1.6;
//         margin-bottom: 25px;
//     }

//     .improve-list {
//         display: grid;
//         grid-template-cols: 1fr 1fr;
//         gap: 12px;
//     }

//     .list-item {
//         font-size: 11px;
//         color: white;
//         font-weight: 700;
//         text-transform: uppercase;
//         letter-spacing: 0.5px;
//         display: flex;
//         align-items: center;
//         gap: 8px;
//     }

//     .dot {
//         width: 4px;
//         height: 4px;
//         background: ${props => props.accent};
//         border-radius: 50%;
//     }

//     .scanner-line {
//         position: absolute;
//         top: 0;
//         left: 0;
//         width: 100%;
//         height: 2px;
//         background: ${props => props.accent};
//         box-shadow: 0 0 15px ${props => props.accent};
//         opacity: 0;
//         z-index: 100;
//     }

//     /* HOVER INTERACTION */
//     &:hover .drawer-wrapper {
//         background: rgba(255, 255, 255, 0.07);
//         border: 1px solid rgba(255, 255, 255, 0.2);
//         box-shadow: 0 40px 80px -20px rgba(0,0,0,0.5);
//         transform: translateY(-5px);
//     }

//     &:hover .drawer-content {
//         max-height: 400px;
//         opacity: 1;
//         transform: rotateX(0deg);
//     }

//     &:hover .icon-ring {
//         background: ${props => props.accent};
//         color: #01014C;
//         transform: scale(1.1) rotate(10deg);
//     }

//     &:hover .corner-arrow {
//         color: white;
//         transform: rotate(-45deg);
//     }

//     &:hover .scanner-line {
//         animation: scan 2s linear infinite;
//         opacity: 1;
//     }

//     @keyframes scan {
//         0% { top: 0; opacity: 0; }
//         10% { opacity: 1; }
//         90% { opacity: 1; }
//         100% { top: 100%; opacity: 0; }
//     }
// `;

// export default WhatWeSolve;